import { useAppUser } from '../admin/AppUserContext';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import {
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  FileText,
  Globe2,
  HelpCircle,
  LayoutDashboard,
  Lightbulb,
  Loader2,
  Menu,
  Plus,
  Search,
  Settings,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';
import {
  authApi,
  billingApi,
  companiesApi,
  tasksApi,
  type Company,
  type CurrentUser,
} from '../services/api';
import { analytics } from '../services/analytics';
import { PLANS, PRICING_CONFIG } from '../config/pricing';
import { CLOUDFLARE_STATIC_ASSETS } from '../config/cloudflareAssets';
import NotificationBell from '../components/NotificationBell';
import {
  DASHBOARD_STAGES,
  filterAndSortCompanies,
  getNextStage,
  getPlanningProgress,
  getPlanningStatus,
  type DashboardProgress,
} from '../utils/dashboard';
import { loadCompanyProgress } from '../utils/companyProgress';
import './DashboardPage.css';

interface DashboardCompany extends Company {
  progress: DashboardProgress | null;
  strategyProgress: number | null;
  tasksCompleted: number | null;
  totalTasks: number | null;
  membersCount: number | null;
}

const stageIcons = [Target, Globe2, BarChart3, Lightbulb, CheckCircle2];

function CompanyAvatar({ company }: { company: Company }) {
  return (
    <span className="dashboard-company-avatar" aria-hidden="true">
      {company.logo ? <img src={company.logo} alt="" /> : company.name.slice(0, 2).toUpperCase()}
    </span>
  );
}

function DashboardShell({
  children,
  companies,
  currentUser,
  canCreate = false,
}: {
  children: ReactNode;
  companies: Company[];
  currentUser: CurrentUser | null;
  canCreate?: boolean;
}) {
  const { user, isSupportSession } = useAppUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigation = (
    <>
      <p className="dashboard-nav-label">Workspace</p>
      <nav aria-label="Workspace navigation" className="dashboard-nav">
        <Link
          to="/dashboard"
          aria-current="page"
          className="is-active"
          onClick={() => setMenuOpen(false)}
        >
          <LayoutDashboard size={19} /> Overview
        </Link>
        <Link to="/adviser" onClick={() => setMenuOpen(false)}>
          <Building2 size={19} />{' '}
          {currentUser?.hasAdviserAccess ? 'Adviser workspace' : 'Adviser programme'}
        </Link>
        <Link to="/billing" onClick={() => setMenuOpen(false)}>
          <CreditCard size={19} /> Subscription
        </Link>
        <Link to="/user-profile" onClick={() => setMenuOpen(false)}>
          <Settings size={19} /> Account settings
        </Link>
        {currentUser?.superUser && (
          <Link to="/admin" onClick={() => setMenuOpen(false)}>
            <Settings size={19} /> Application admin
          </Link>
        )}
      </nav>
      {companies.length > 0 && (
        <>
          <p className="dashboard-nav-label dashboard-company-nav-label">
            Your companies <span>{companies.length}</span>
          </p>
          <nav aria-label="Company shortcuts" className="dashboard-nav dashboard-company-nav">
            {companies.slice(0, 5).map((company) => (
              <Link
                to={`/company/${company.id}`}
                key={company.id}
                onClick={() => setMenuOpen(false)}
              >
                <CompanyAvatar company={company} />
                <span>{company.name}</span>
                <ChevronRight size={14} />
              </Link>
            ))}
            {companies.length > 5 && (
              <a href="#your-companies" onClick={() => setMenuOpen(false)}>
                See all companies <ArrowRight size={15} />
              </a>
            )}
          </nav>
        </>
      )}
      <div className="dashboard-sidebar-bottom">
        <div className="dashboard-sidebar-tip">
          <span className="dashboard-icon purple">
            <Sparkles size={19} />
          </span>
          <h3>Big ideas. Clear next steps.</h3>
          <p>One connected journey from your vision to getting things done.</p>
          <Link to="/strategic-framework" onClick={() => setMenuOpen(false)}>
            Explore the framework <ArrowUpRight size={14} />
          </Link>
        </div>
        <Link to="/contact" className="dashboard-help-link" onClick={() => setMenuOpen(false)}>
          <HelpCircle size={18} /> Help & support
        </Link>
      </div>
    </>
  );

  return (
    <div className="dashboard-workspace">
      <a className="dashboard-skip-link" href="#dashboard-main">
        Skip to dashboard
      </a>
      <aside className="dashboard-sidebar">
        <Link to="/dashboard" className="dashboard-brand" aria-label="StratPlan dashboard">
          <img src={CLOUDFLARE_STATIC_ASSETS.LOGO_LIGHT} alt="StratPlan" />
        </Link>
        <div className="dashboard-sidebar-body">{navigation}</div>
      </aside>
      <div className="dashboard-page">
        <header className="dashboard-topbar">
          <div className="dashboard-topbar-title">
            <button
              type="button"
              className="dashboard-mobile-toggle dashboard-icon-button"
              aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
              aria-expanded={menuOpen}
              aria-controls="dashboard-mobile-navigation"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X size={21} /> : <Menu size={21} />}
            </button>
            <span className="dashboard-breadcrumb">
              Workspace <ChevronRight size={14} />
            </span>
            <span>Overview</span>
          </div>
          <div className="dashboard-topbar-actions">
            {canCreate && (
              <Link
                to="/company/setup"
                className="dashboard-button dashboard-button-primary dashboard-new-company"
              >
                <Plus size={17} />
                <span>New company</span>
              </Link>
            )}
            <NotificationBell />
            <div className="dashboard-user-menu">
              <div className="dashboard-user-name">
                <strong>{user?.fullName || user?.firstName || 'Your account'}</strong>
                <span>Personal workspace</span>
              </div>
              {!isSupportSession && (
                <UserButton
                  appearance={{ elements: { avatarBox: 'h-9 w-9' } }}
                  userProfileMode="navigation"
                  userProfileUrl="/user-profile"
                  afterSignOutUrl="/"
                >
                  <UserButton.MenuItems>
                    <UserButton.Link
                      label="Subscriptions"
                      labelIcon={<CreditCard size={16} />}
                      href="/billing"
                    />
                  </UserButton.MenuItems>
                </UserButton>
              )}
            </div>
          </div>
        </header>
        {menuOpen && (
          <div id="dashboard-mobile-navigation" className="dashboard-mobile-navigation">
            {navigation}
          </div>
        )}
        <main id="dashboard-main" className="dashboard-main" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAppUser();
  const userId = user?.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyMetrics, setCompanyMetrics] = useState<DashboardCompany[]>([]);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [hasFounderSubscription, setHasFounderSubscription] = useState(false);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [isAwaitingWebhook, setIsAwaitingWebhook] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'newest' | 'name' | 'progress'>('newest');
  const [reloadKey, setReloadKey] = useState(0);
  const hasSyncedThisSession = useRef(false);

  // Keep checkout activation and purchase tracking intact while the webhook arrives.
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success') return;
    setIsAwaitingWebhook(true);
    const startedAt = Date.now();
    const pollInterval = setInterval(async () => {
      try {
        const res = await authApi.getCurrentUser();
        if (res.data?.isFounder || res.data?.superUser) {
          clearInterval(pollInterval);
          if (!sessionStorage.getItem('ga_founder_purchase_tracked')) {
            sessionStorage.setItem('ga_founder_purchase_tracked', '1');
            try {
              const billingRes = await billingApi.getUserBillingInfo();
              const amount =
                billingRes.data?.actualAmount != null
                  ? billingRes.data.actualAmount / 100
                  : (PRICING_CONFIG[PLANS.FOUNDER].price.launch?.amount ?? 197);
              analytics.trackSubscription(amount, 'USD', 'Founder Annual');
            } catch {
              analytics.trackSubscription(
                PRICING_CONFIG[PLANS.FOUNDER].price.launch?.amount ?? 197,
                'USD',
                'Founder Annual'
              );
            }
          }
          navigate('/dashboard', { replace: true });
          window.location.reload();
        } else if (Date.now() - startedAt > 30_000) {
          clearInterval(pollInterval);
          setIsAwaitingWebhook(false);
        }
      } catch {
        if (Date.now() - startedAt > 30_000) {
          clearInterval(pollInterval);
          setIsAwaitingWebhook(false);
        }
      }
    }, 2_000);
    return () => clearInterval(pollInterval);
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [userResponse, companiesResponse] = await Promise.all([
          authApi.getCurrentUser(),
          companiesApi.list(),
        ]);
        if (!userResponse.success || !userResponse.data)
          throw new Error(userResponse.error || 'We couldn’t load your account. Please try again.');
        if (!companiesResponse.success || !companiesResponse.data)
          throw new Error(
            companiesResponse.error || 'We couldn’t load your companies. Please try again.'
          );
        let resolvedCompanies = companiesResponse.data;
        if (resolvedCompanies.length === 0 && !hasSyncedThisSession.current) {
          hasSyncedThisSession.current = true;
          let syncedCount = 0;
          try {
            const sync = await authApi.syncMemberships();
            if (sync.success) syncedCount = sync.data?.syncedCount ?? 0;
          } catch (syncError) {
            // Membership sync is a fallback; a failure doesn't block an empty workspace.
            console.error('Membership sync failed:', syncError);
          }
          if (syncedCount > 0) {
            const refreshed = await companiesApi.list();
            if (!refreshed.success || !refreshed.data)
              throw new Error(refreshed.error || 'Couldn’t load your synced companies.');
            resolvedCompanies = refreshed.data;
          }
        }
        const account = userResponse.data;
        const hasAccess =
          account.superUser ||
          account.hasAdviserAccess ||
          (account.isFounder &&
            ['active', 'trialing'].includes(account.founderSubscriptionStatus || '')) ||
          resolvedCompanies.some((company) =>
            ['pro', 'enterprise'].includes(company.subscriptionTier)
          );
        if (cancelled) return;
        setCurrentUser(account);
        setCompanies(resolvedCompanies);
        setHasFounderSubscription(hasAccess);
        if (hasAccess) {
          const metrics = await Promise.all(
            resolvedCompanies.map(async (company): Promise<DashboardCompany> => {
              const [progressResult, membersResult, tasksResult] = await Promise.allSettled([
                loadCompanyProgress(company.id),
                companiesApi.getTeamMembers(company.id),
                tasksApi.list({ company: company.id }),
              ]);
              const progress =
                progressResult.status === 'fulfilled' ? progressResult.value.progress : null;
              const members =
                membersResult.status === 'fulfilled' && membersResult.value.success
                  ? (membersResult.value.data ?? null)
                  : null;
              const tasks =
                tasksResult.status === 'fulfilled' && tasksResult.value.success
                  ? (tasksResult.value.data?.all ?? null)
                  : null;
              return {
                ...company,
                progress,
                strategyProgress: progress ? getPlanningProgress(progress) : null,
                membersCount: members?.length ?? null,
                totalTasks: tasks?.length ?? null,
                tasksCompleted: tasks
                  ? tasks.filter((task) => task.status === 'completed').length
                  : null,
              };
            })
          );
          if (!cancelled) setCompanyMetrics(metrics);
        } else {
          setCompanyMetrics([]);
        }
      } catch (err) {
        if (!cancelled)
          setError(
            err instanceof Error
              ? err.message
              : 'We couldn’t load your workspace. Please try again.'
          );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [userId, reloadKey]);

  const handleStartFounderCheckout = async () => {
    setIsStartingCheckout(true);
    setCheckoutError(null);
    try {
      const response = await billingApi.createFounderCheckoutSessionForUser();
      if (response.success && response.data?.url) window.location.href = response.data.url;
      else {
        setCheckoutError(response.error || 'Failed to start checkout. Please try again.');
        setIsStartingCheckout(false);
      }
    } catch {
      setCheckoutError('Something went wrong. Please try again.');
      setIsStartingCheckout(false);
    }
  };

  const shellProps = {
    companies,
    currentUser,
    canCreate:
      !isLoading &&
      !error &&
      hasFounderSubscription &&
      !isAwaitingWebhook &&
      Boolean(
        currentUser?.superUser ||
        currentUser?.isFounder ||
        companies.some(
          (company) =>
            !company.isDemo &&
            !company.adviserPracticeId &&
            ['pro', 'enterprise'].includes(company.subscriptionTier)
        )
      ),
  };
  const greeting = user?.firstName
    ? `Welcome back, ${user.firstName}.`
    : 'Welcome to your workspace.';

  if (isAwaitingWebhook || isLoading)
    return (
      <DashboardShell {...shellProps}>
        <div className="dashboard-loading" role="status">
          <Loader2 size={30} className="dashboard-spinner" />
          <h1>
            {isAwaitingWebhook
              ? 'Activating your Founder subscription…'
              : 'Getting your workspace ready…'}
          </h1>
          <p>
            {isAwaitingWebhook
              ? 'Your payment was successful. Your workspace will open as soon as activation is confirmed.'
              : 'Bringing your companies, plans and tasks together.'}
          </p>
        </div>
      </DashboardShell>
    );

  // A request failure must not be mistaken for a missing subscription.
  if (error)
    return (
      <DashboardShell {...shellProps}>
        <div className="dashboard-empty-state" role="alert">
          <span className="dashboard-icon blue">
            <Building2 size={26} />
          </span>
          <h1>Let’s get your workspace back.</h1>
          <p>{error}</p>
          <button
            type="button"
            className="dashboard-button dashboard-button-primary"
            onClick={() => setReloadKey((key) => key + 1)}
          >
            Try again <ArrowRight size={17} />
          </button>
        </div>
      </DashboardShell>
    );

  if (!hasFounderSubscription) {
    const founder = PRICING_CONFIG[PLANS.FOUNDER];
    return (
      <DashboardShell {...shellProps}>
        <div className="dashboard-welcome">
          <p className="dashboard-eyebrow">Your next chapter</p>
          <h1>{greeting}</h1>
          <p>Give your company a clear direction, then bring your plan to life.</p>
        </div>
        <section className="dashboard-onboarding-card">
          <div className="dashboard-onboarding-copy">
            <span className="dashboard-icon purple">
              <Sparkles size={26} />
            </span>
            <p className="dashboard-eyebrow">Founder workspace</p>
            <h2>Everything you need to build your strategy.</h2>
            <p>
              Connect your vision, business analysis, people and priorities in one guided workspace,
              with AI to help you move forward.
            </p>
            <div className="dashboard-onboarding-features">
              {[
                [Target, 'Define your direction', 'Vision, mission, values and objectives'],
                [BarChart3, 'Understand your business', 'SWOT, PESTEL and Five Forces'],
                [Users, 'Connect your people', 'Visual organization and team ownership'],
                [
                  Lightbulb,
                  'Build a plan you can act on',
                  'Guided strategy formulation and reports',
                ],
              ].map(([Icon, title, description]) => {
                const FeatureIcon = Icon as typeof Target;
                return (
                  <div key={String(title)}>
                    <FeatureIcon size={20} />
                    <span>
                      <strong>{String(title)}</strong>
                      <small>{String(description)}</small>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="dashboard-onboarding-offer">
            <span className="dashboard-access-badge">
              <Sparkles size={14} /> Founder access
            </span>
            <h3>Your strategy starts here.</h3>
            <p className="dashboard-offer-amount">
              ${founder.price.launch?.amount ?? founder.price.standard.amount}
            </p>
            <p>{founder.price.launch ? 'for your first year' : 'per year'}</p>
            {founder.price.launch && (
              <p className="dashboard-renewal">
                Renews at ${founder.price.standard.amount}/year after the first year.
              </p>
            )}
            <button
              type="button"
              className="dashboard-button dashboard-button-primary"
              onClick={handleStartFounderCheckout}
              disabled={isStartingCheckout}
            >
              {isStartingCheckout ? 'Opening checkout…' : 'Get started with Founder'}
              <ArrowRight size={17} />
            </button>
            {checkoutError && (
              <p role="alert" className="dashboard-checkout-error">
                {checkoutError}
              </p>
            )}
            <Link to="/pricing" className="dashboard-text-link">
              See what’s included <ArrowUpRight size={15} />
            </Link>
            <small>
              <CheckCircle2 size={14} /> Secure checkout · Cancel anytime
            </small>
          </div>
        </section>
      </DashboardShell>
    );
  }

  const sortedCompanies = filterAndSortCompanies(companyMetrics, '', 'newest');
  const featuredCompany = sortedCompanies.find((company) => company.progress !== null);
  const nextStage = featuredCompany ? getNextStage(featuredCompany.progress!) : null;
  const displayedCompanies = filterAndSortCompanies(companyMetrics, search, sort);
  const knownProgress = companyMetrics.filter((company) => company.strategyProgress !== null);
  const averageProgress = knownProgress.length
    ? Math.min(
        knownProgress.every((company) => company.strategyProgress === 100) ? 100 : 99,
        Math.round(
          knownProgress.reduce((sum, company) => sum + company.strategyProgress!, 0) /
            knownProgress.length
        )
      )
    : null;
  const hasTaskData = companyMetrics.every((company) => company.totalTasks !== null);
  const totalTasks = hasTaskData
    ? companyMetrics.reduce((sum, company) => sum + company.totalTasks!, 0)
    : null;
  const completedTasks = hasTaskData
    ? companyMetrics.reduce((sum, company) => sum + company.tasksCompleted!, 0)
    : null;
  const hasUnavailableData = companyMetrics.some(
    (company) =>
      company.progress === null || company.membersCount === null || company.totalTasks === null
  );
  const nextStageIndex = nextStage
    ? DASHBOARD_STAGES.findIndex((stage) => stage.key === nextStage.key)
    : 0;
  const nextStageAction =
    nextStage?.key === 'executionProgress'
      ? 'Open execution'
      : `${featuredCompany?.progress?.[nextStage?.key] > 0 ? 'Continue' : 'Start'} ${nextStage?.title.toLowerCase()}`;

  return (
    <DashboardShell {...shellProps}>
      <div className="dashboard-welcome">
        <div>
          <p className="dashboard-eyebrow">Your strategy, connected</p>
          <h1>{greeting}</h1>
          <p>A little clarity. A clear next step. Let’s move your business forward.</p>
        </div>
        <span className="dashboard-workspace-badge">
          <span /> Your workspace
        </span>
      </div>

      {companies.length === 0 ? (
        <section className="dashboard-empty-state dashboard-first-company">
          <span className="dashboard-icon blue">
            <Building2 size={30} />
          </span>
          <p className="dashboard-eyebrow">Start with your company</p>
          <h2>Your next big idea starts here.</h2>
          <p>
            Create your company workspace. Then define your direction, understand your business and
            build a plan your team can put to work.
          </p>
          <Link to="/company/setup" className="dashboard-button dashboard-button-primary">
            <Plus size={18} /> Create your first company
          </Link>
          <div className="dashboard-empty-journey">
            {DASHBOARD_STAGES.map((stage, index) => (
              <span key={stage.key}>
                <b>{index + 1}</b>
                {stage.title}
              </span>
            ))}
          </div>
        </section>
      ) : (
        <>
          {featuredCompany && nextStage && (
            <section className="dashboard-next-step" aria-labelledby="dashboard-next-step-title">
              <div className="dashboard-next-step-copy">
                <p className="dashboard-eyebrow">
                  <Sparkles size={14} /> Suggested next step <span>· {featuredCompany.name}</span>
                </p>
                <h2 id="dashboard-next-step-title">
                  {nextStageIndex === 0
                    ? 'Great strategy starts with a clear direction.'
                    : nextStageIndex === 4
                      ? 'Turn your plan into progress.'
                      : `Keep building. Next up: ${nextStage.title.toLowerCase()}.`}
                </h2>
                <p>{nextStage.description}</p>
                <Link
                  className="dashboard-button dashboard-button-primary"
                  to={`/company/${featuredCompany.id}/${nextStage.path}`}
                >
                  {nextStageAction}
                  <ArrowRight size={17} />
                </Link>
                <Link
                  className="dashboard-next-step-secondary"
                  to={`/company/${featuredCompany.id}/strategy`}
                >
                  View full plan <ArrowUpRight size={15} />
                </Link>
              </div>
              <div className="dashboard-journey-preview" aria-hidden="true">
                <div className="dashboard-journey-preview-heading">
                  <span>
                    <Target size={15} /> From direction to delivery
                  </span>
                  <Sparkles size={16} />
                </div>
                <div className="dashboard-journey-preview-stages">
                  {['Vision', 'Strategy', 'Action'].map((label, index) => (
                    <div key={label}>
                      <span
                        className={`dashboard-icon ${index === 1 ? 'purple' : index === 2 ? 'mint' : 'blue'}`}
                      >
                        {index === 0 ? (
                          <Target size={21} />
                        ) : index === 1 ? (
                          <Lightbulb size={21} />
                        ) : (
                          <CheckCircle2 size={21} />
                        )}
                      </span>
                      <strong>{label}</strong>
                      <span className="dashboard-preview-line" />
                      <span className="dashboard-preview-line short" />
                    </div>
                  ))}
                </div>
                <div className="dashboard-journey-preview-footer">
                  <span className="dashboard-connected-dot" /> One plan. Everything connected.
                </div>
              </div>
            </section>
          )}

          <section className="dashboard-summary" aria-label="Workspace summary">
            {[
              {
                icon: Building2,
                color: 'blue',
                label: 'Companies',
                value: companies.length,
                detail: `${companies.length === 1 ? 'One connected workspace' : 'Workspaces you belong to'}`,
              },
              {
                icon: Target,
                color: 'purple',
                label: 'Planning progress',
                value: averageProgress === null ? '—' : `${averageProgress}%`,
                detail:
                  knownProgress.length === companyMetrics.length
                    ? 'Average of the four planning stages'
                    : `Across ${knownProgress.length} available plans`,
              },
              {
                icon: CheckCircle2,
                color: 'mint',
                label: 'Open tasks',
                value: totalTasks === null ? '—' : totalTasks - completedTasks!,
                detail:
                  totalTasks === null
                    ? 'Task data unavailable'
                    : totalTasks === 0
                      ? 'No tasks created yet'
                      : 'Tasks still to complete',
              },
              {
                icon: Check,
                color: 'peach',
                label: 'Completed tasks',
                value: completedTasks ?? '—',
                detail:
                  totalTasks === null
                    ? 'Task data unavailable'
                    : `${completedTasks} of ${totalTasks} company tasks`,
              },
            ].map((metric) => (
              <div className="dashboard-summary-card" key={metric.label}>
                <div>
                  <span>{metric.label}</span>
                  <strong>{metric.value}</strong>
                  <small>{metric.detail}</small>
                </div>
                <span className={`dashboard-icon ${metric.color}`}>
                  <metric.icon size={20} />
                </span>
              </div>
            ))}
          </section>

          {hasUnavailableData && (
            <div className="dashboard-data-notice" role="status">
              <span>Some company details couldn’t load. Your work is still saved.</span>
              <button type="button" onClick={() => setReloadKey((key) => key + 1)}>
                Retry loading
              </button>
            </div>
          )}

          <div className="dashboard-content-grid">
            <section
              id="your-companies"
              className="dashboard-companies-section"
              aria-labelledby="dashboard-companies-title"
            >
              <div className="dashboard-section-heading">
                <div>
                  <h2 id="dashboard-companies-title">
                    Your companies <span>{companies.length}</span>
                  </h2>
                  <p>Open a workspace and pick up your plan.</p>
                </div>
              </div>
              <div className="dashboard-company-controls">
                <div className="dashboard-search">
                  <Search size={17} />
                  <label className="sr-only" htmlFor="dashboard-company-search">
                    Search companies
                  </label>
                  <input
                    id="dashboard-company-search"
                    type="search"
                    placeholder="Find a company…"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      aria-label="Clear company search"
                      onClick={() => setSearch('')}
                    >
                      <X size={15} />
                    </button>
                  )}
                </div>
                <label className="sr-only" htmlFor="dashboard-company-sort">
                  Sort companies
                </label>
                <select
                  id="dashboard-company-sort"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as typeof sort)}
                >
                  <option value="newest">Newest first</option>
                  <option value="name">Name A–Z</option>
                  <option value="progress">Most progress</option>
                </select>
              </div>
              <p className="sr-only" aria-live="polite">
                {displayedCompanies.length}{' '}
                {displayedCompanies.length === 1 ? 'company' : 'companies'} shown
              </p>
              {displayedCompanies.length === 0 ? (
                <div className="dashboard-search-empty">
                  <Search size={26} />
                  <h3>No companies match “{search}”.</h3>
                  <p>Try another name or clear your search.</p>
                  <button
                    type="button"
                    className="dashboard-button dashboard-button-secondary"
                    onClick={() => setSearch('')}
                  >
                    Show all companies
                  </button>
                </div>
              ) : (
                <div
                  className={`dashboard-company-grid ${companies.length === 1 ? 'dashboard-single-company' : ''}`}
                >
                  {displayedCompanies.map((company) => {
                    const companyNextStage = company.progress
                      ? getNextStage(company.progress)
                      : null;
                    const progressStatus =
                      company.strategyProgress === null
                        ? 'Progress unavailable'
                        : getPlanningStatus(company.strategyProgress);
                    return (
                      <article className="dashboard-company-card" key={company.id}>
                        <div className="dashboard-company-card-heading">
                          <CompanyAvatar company={company} />
                          <div>
                            <h3>
                              <Link to={`/company/${company.id}`}>{company.name}</Link>
                            </h3>
                            <p>
                              <span>{company.role || 'member'}</span>
                              {company.industry && <> · {company.industry}</>}
                            </p>
                          </div>
                          <span
                            className={`dashboard-plan-status ${company.strategyProgress === 100 ? 'complete' : company.strategyProgress ? 'started' : ''}`}
                          >
                            {progressStatus}
                          </span>
                        </div>
                        <div className="dashboard-company-progress">
                          <div>
                            <span>Strategic plan</span>
                            <strong>
                              {company.strategyProgress === null
                                ? 'Unavailable'
                                : `${company.strategyProgress}% complete`}
                            </strong>
                          </div>
                          <div
                            className="dashboard-progress-track"
                            {...(company.strategyProgress !== null
                              ? {
                                  role: 'progressbar',
                                  'aria-label': `${company.name} planning progress`,
                                  'aria-valuenow': company.strategyProgress,
                                  'aria-valuemin': 0,
                                  'aria-valuemax': 100,
                                }
                              : {})}
                          >
                            <span style={{ width: `${company.strategyProgress ?? 0}%` }} />
                          </div>
                        </div>
                        <div className="dashboard-company-stages">
                          {DASHBOARD_STAGES.slice(0, 4).map((stage, index) => (
                            <Link
                              to={`/company/${company.id}/${stage.path}`}
                              key={stage.key}
                              aria-label={`${stage.title} for ${company.name}${company.progress ? `, ${company.progress[stage.key]}% complete` : ''}`}
                            >
                              <span
                                className={
                                  company.progress?.[stage.key] >= 100
                                    ? 'complete'
                                    : companyNextStage?.key === stage.key
                                      ? 'current'
                                      : ''
                                }
                              >
                                {company.progress?.[stage.key] >= 100 ? (
                                  <Check size={11} />
                                ) : (
                                  index + 1
                                )}
                              </span>
                              {['Foundation', 'External', 'Internal', 'Strategy'][index]}
                            </Link>
                          ))}
                        </div>
                        <div className="dashboard-company-shortcuts">
                          <Link to={`/company/${company.id}/organization`}>
                            <Users size={16} />
                            <span>
                              People
                              {company.membersCount !== null && (
                                <small>
                                  {company.membersCount}{' '}
                                  {company.membersCount === 1 ? 'member' : 'members'}
                                </small>
                              )}
                            </span>
                            <ArrowUpRight size={13} />
                          </Link>
                          <Link to={`/company/${company.id}/execution`}>
                            <CheckCircle2 size={16} />
                            <span>
                              Execution
                              <small>
                                {company.totalTasks === null
                                  ? 'View tasks'
                                  : company.totalTasks === 0
                                    ? 'Create your first task'
                                    : `${company.tasksCompleted}/${company.totalTasks} tasks done`}
                              </small>
                            </span>
                            <ArrowUpRight size={13} />
                          </Link>
                          <Link to={`/company/${company.id}/visual-report`}>
                            <FileText size={16} />
                            <span>
                              Reports<small>See the whole picture</small>
                            </span>
                            <ArrowUpRight size={13} />
                          </Link>
                        </div>
                        <div className="dashboard-company-card-footer">
                          <Link to={`/company/${company.id}`} className="dashboard-text-link">
                            Company overview <ArrowUpRight size={14} />
                          </Link>
                          <Link
                            to={
                              companyNextStage
                                ? `/company/${company.id}/${companyNextStage.path}`
                                : `/company/${company.id}/strategy`
                            }
                            className="dashboard-button dashboard-button-secondary"
                          >
                            {company.strategyProgress === 0
                              ? 'Start planning'
                              : companyNextStage?.key === 'executionProgress'
                                ? 'Open execution'
                                : 'Continue planning'}
                            <ArrowRight size={15} />
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            {featuredCompany && (
              <aside className="dashboard-guide" aria-labelledby="dashboard-guide-title">
                <div className="dashboard-guide-heading">
                  <span className="dashboard-icon purple">
                    <Target size={19} />
                  </span>
                  <div>
                    <h2 id="dashboard-guide-title">Your path to progress</h2>
                    <p>{featuredCompany.name}</p>
                  </div>
                </div>
                <ol>
                  {DASHBOARD_STAGES.map((stage, index) => {
                    const StageIcon = stageIcons[index];
                    const progress = featuredCompany.progress![stage.key];
                    const isNext = nextStage?.key === stage.key;
                    return (
                      <li key={stage.key}>
                        <Link
                          to={`/company/${featuredCompany.id}/${stage.path}`}
                          className={isNext ? 'is-next' : ''}
                        >
                          <span
                            className={`dashboard-guide-step ${progress >= 100 ? 'complete' : isNext ? 'current' : ''}`}
                          >
                            {progress >= 100 ? <Check size={15} /> : <StageIcon size={17} />}
                          </span>
                          <span className="dashboard-guide-step-copy">
                            <strong>{stage.title}</strong>
                            <small>
                              {index === 4
                                ? 'Projects, tasks and ownership'
                                : progress >= 100
                                  ? 'Complete'
                                  : progress > 0
                                    ? `${progress}% complete`
                                    : 'Not started'}
                            </small>
                          </span>
                          {isNext ? (
                            <span className="dashboard-next-label">Next</span>
                          ) : (
                            <ChevronRight size={13} />
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ol>
                <div className="dashboard-ai-tip">
                  <Sparkles size={18} />
                  <div>
                    <strong>A little help from AI.</strong>
                    <p>
                      Use your company context to draft ideas, explore options and find your next
                      move.
                    </p>
                    <Link to={`/company/${featuredCompany.id}/strategy/foundation/vision`}>
                      Explore AI-assisted planning <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
              </aside>
            )}
          </div>
          <p className="dashboard-footer-note">
            <Sparkles size={14} /> Small steps today. A stronger business tomorrow.
          </p>
        </>
      )}
    </DashboardShell>
  );
}
